import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'motion/react';
import {
  CategoryScale,
  Chart,
  Filler,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  type ChartConfiguration,
} from 'chart.js';
import { t, money, dateOnly } from '../i18n/index.ts';
import { api } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { Avatar, ErrorView, Loading, Money } from '../components/ui.tsx';
import type { StatsView } from '../../server/domain/stats.ts';import { useThemeKey } from '../lib/theme.ts';


Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Filler);

const MAX_SERIES = 4;

function cssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}


interface Series {
  userId: string;
  name: string;
  slot: number;
  /** Cumulative net cents per timeline point (null before the first tournament). */
  values: (number | null)[];
  played: boolean[];
}

function buildSeries(stats: StatsView, userId: string, name: string, slot: number): Series {
  const results = new Map((stats.results[userId] ?? []).map((r) => [r.tournamentId, r.netCents]));
  let cumulative: number | null = null;
  const values: (number | null)[] = [];
  const played: boolean[] = [];
  for (const point of stats.timeline) {
    const net = results.get(point.tournamentId);
    if (net !== undefined) cumulative = (cumulative ?? 0) + net;
    values.push(cumulative);
    played.push(net !== undefined);
  }
  return { userId, name, slot, values, played };
}

function ProgressChart({ stats, series }: { stats: StatsView; series: Series[] }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  // Re-draws when the theme or light/dark changes, so the chart picks up the new colours.
  const scheme = useThemeKey();
  useEffect(() => {
    if (!canvas.current) return;
    const ink = cssVar('--ink');
    const muted = cssVar('--ink-3');
    const grid = cssVar('--line');
    const surface = cssVar('--paper');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const config: ChartConfiguration<'line', (number | null)[], string> = {
      type: 'line',
      data: {
        labels: stats.timeline.map((p) => p.name),
        datasets: series.map((s) => {
          const color = cssVar(`--series-${s.slot}`);
          return {
            label: s.name,
            data: s.values.map((v) => (v === null ? null : v / 100)),
            borderColor: color,
            backgroundColor: color,
            borderWidth: 2,
            tension: 0,
            spanGaps: false,
            // Markers only where the player actually played; a 2px surface ring separates overlaps.
            pointRadius: s.played.map((p) => (p ? 4 : 0)),
            pointHoverRadius: s.played.map((p) => (p ? 6 : 0)),
            pointBorderColor: surface,
            pointBorderWidth: 2,
            pointHitRadius: 12,
          };
        }),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: reduced ? false : { duration: 450, easing: 'easeOutCubic' },
        interaction: { mode: 'index', intersect: false },
        layout: { padding: { top: 8, right: 8 } },
        scales: {
          x: {
            grid: { display: false },
            border: { color: grid },
            ticks: { color: muted, maxRotation: 0, autoSkip: true, autoSkipPadding: 16, font: { family: cssVar('--font-body') } },
          },
          y: {
            border: { display: false },
            grid: {
              // A stronger zero line: above it you are winning, below it losing.
              color: (ctx) => (ctx.tick.value === 0 ? muted : grid),
              lineWidth: (ctx) => (ctx.tick.value === 0 ? 1.5 : 1),
            },
            ticks: {
              color: muted,
              font: { family: cssVar('--font-body') },
              callback: (v) => money(Math.round(Number(v) * 100)),
            },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: cssVar('--paper'),
            titleColor: ink,
            bodyColor: ink,
            borderColor: cssVar('--line-strong'),
            borderWidth: 1,
            padding: 10,
            boxPadding: 4,
            usePointStyle: true,
            callbacks: {
              title: (items) => {
                const p = stats.timeline[items[0]?.dataIndex ?? 0];
                return p ? `${p.name} (${dateOnly(p.finishedAt)})` : '';
              },
              label: (item) => {
                const s = series[item.datasetIndex]!;
                if (item.raw === null) return '';
                const tournament = stats.timeline[item.dataIndex]!;
                const net = stats.results[s.userId]?.find((r) => r.tournamentId === tournament.tournamentId)?.netCents;
                const delta = net === undefined ? t('stats.notPlayed') : `${net > 0 ? '+' : ''}${money(net)}`;
                return ` ${s.name}: ${money(Math.round(Number(item.raw) * 100))} (${delta})`;
              },
            },
          },
        },
      },
    };
    const chart = new Chart(canvas.current, config);
    return () => chart.destroy();
  }, [stats, series, scheme]);
  return (
    <div className="chart-box">
      <canvas ref={canvas} role="img" aria-label={t('stats.chartLabel')} />
    </div>
  );
}

function Tile({ label, children, delay }: { label: string; children: React.ReactNode; delay: number }) {
  return (
    <motion.div className="stat-tile" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay, duration: 0.25, ease: 'easeOut' }}>
      <span className="stat-label">{label}</span>
      <span className="stat-value">{children}</span>
    </motion.div>
  );
}

const pct = (a: number, b: number) => (b > 0 ? `${Math.round((a / b) * 100)} %` : '–');

export function StatsPage() {
  const { me } = useSession();
  const q = useQuery({ queryKey: ['stats'], queryFn: () => api.get<StatsView>('/api/stats') });
  // Compared players keep their colour slot while selected (colour follows the person).
  const [compare, setCompare] = useState<{ userId: string; slot: number }[]>([]);
  const series = useMemo(() => {
    if (!q.data || !me) return [];
    const names = new Map(q.data.leaderboard.map((p) => [p.user.id, p.user.displayName]));
    return [
      buildSeries(q.data, me.id, me.displayName, 1),
      ...compare.map((c) => buildSeries(q.data, c.userId, names.get(c.userId) ?? '?', c.slot)),
    ];
  }, [q.data, me, compare]);

  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorView error={q.error} onRetry={() => q.refetch()} />;
  const s = q.data;
  const toggle = (userId: string) => {
    setCompare((cur) => {
      if (cur.some((c) => c.userId === userId)) return cur.filter((c) => c.userId !== userId);
      if (cur.length >= MAX_SERIES - 1) return cur;
      const free = [2, 3, 4].find((slot) => !cur.some((c) => c.slot === slot))!;
      return [...cur, { userId, slot: free }];
    });
  };

  return (
    <div className="page">
      <h1>{t('stats.title')}</h1>
      <section className="stat-tiles" aria-label={t('stats.summary')}>
        <Tile label={t('stats.net')} delay={0}>
          <Money cents={s.me.netCents} signed />
        </Tile>
        <Tile label={t('stats.tournaments')} delay={0.03}>
          {s.me.tournaments}
        </Tile>
        <Tile label={t('stats.wins')} delay={0.06}>
          {s.me.wins}
        </Tile>
        <Tile label={t('stats.itm')} delay={0.09}>
          {s.me.itm}
          <small> / {s.me.tournaments}</small>
        </Tile>
        <Tile label={t('stats.roi')} delay={0.12}>
          {s.me.roiPercent === null ? '–' : `${s.me.roiPercent > 0 ? '+' : ''}${s.me.roiPercent.toLocaleString('fi-FI')} %`}
        </Tile>
        <Tile label={t('stats.avgPlace')} delay={0.15}>
          {s.me.averagePlace === null ? '–' : s.me.averagePlace.toLocaleString('fi-FI')}
        </Tile>
      </section>

      <section className="panel" aria-labelledby="chart-h" style={{ marginTop: 20 }}>
        <div className="section-head">
          <h2 id="chart-h">{t('stats.progress')}</h2>
          {series.length > 1 && (
            <ul className="chart-legend" aria-label={t('stats.legend')}>
              {series.map((x) => (
                <li key={x.userId}>
                  <span className="legend-swatch" style={{ background: `var(--series-${x.slot})` }} aria-hidden="true" />
                  {x.name}
                </li>
              ))}
            </ul>
          )}
        </div>
        {s.timeline.length === 0 ? (
          <p className="empty">{t('stats.noData')}</p>
        ) : (
          <>
            <ProgressChart stats={s} series={series} />
            <p className="hint">{t('stats.chartHelp')}</p>
          </>
        )}
      </section>

      <div className="grid-2" style={{ marginTop: 20, alignItems: 'start' }}>
        <section className="panel" aria-labelledby="hands-h">
          <h2 id="hands-h">{t('stats.handsTitle')}</h2>
          <dl className="kv">
            <dt>{t('stats.handsPlayed')}</dt>
            <dd className="num">{s.hands.handsPlayed}</dd>
            <dt>{t('stats.handsWon')}</dt>
            <dd className="num">
              {s.hands.handsWon} ({pct(s.hands.handsWon, s.hands.handsPlayed)})
            </dd>
            <dt>{t('stats.showdowns')}</dt>
            <dd className="num">
              {s.hands.showdowns} ({t('stats.showdownWinRate', { pct: pct(s.hands.showdownsWon, s.hands.showdowns) })})
            </dd>
            <dt>{t('stats.biggestPot')}</dt>
            <dd className="num">{s.hands.biggestPotBb.toLocaleString('fi-FI')} BB</dd>
            <dt>{t('stats.timeouts')}</dt>
            <dd className="num">{s.hands.timeouts}</dd>
            <dt>{t('stats.bestPlace')}</dt>
            <dd className="num">{s.me.bestPlace ? `${s.me.bestPlace}.` : '–'}</dd>
          </dl>
        </section>

        <section className="panel" aria-labelledby="board-h">
          <h2 id="board-h">{t('stats.leaderboard')}</h2>
          <p className="hint">{t('stats.compareHelp', { n: MAX_SERIES - 1 })}</p>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('results.player')}</th>
                  <th className="r hide-sm">{t('stats.tournamentsShort')}</th>
                  <th className="r hide-xs">{t('stats.winsShort')}</th>
                  <th className="r">{t('stats.net')}</th>
                </tr>
              </thead>
              <tbody>
                {s.leaderboard.map((p, i) => {
                  const isMe = p.user.id === me?.id;
                  const selected = compare.find((c) => c.userId === p.user.id);
                  return (
                    <tr key={p.user.id} className={isMe ? 'row-me' : undefined}>
                      <td className="num">{i + 1}.</td>
                      <td>
                        {isMe ? (
                          <span className="compare-name">
                            <span className="legend-swatch" style={{ background: 'var(--series-1)' }} aria-hidden="true" />
                            <Avatar name={p.user.displayName} url={p.user.avatarUrl} size="s" />
                            {p.user.displayName}
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="compare-name compare-toggle"
                            aria-pressed={!!selected}
                            disabled={!selected && compare.length >= MAX_SERIES - 1}
                            onClick={() => toggle(p.user.id)}
                          >
                            <span
                              className="legend-swatch"
                              style={{ background: selected ? `var(--series-${selected.slot})` : 'transparent', borderColor: selected ? 'transparent' : 'var(--line-strong)' }}
                              aria-hidden="true"
                            />
                            <Avatar name={p.user.displayName} url={p.user.avatarUrl} size="s" />
                            {p.user.displayName}
                          </button>
                        )}
                      </td>
                      <td className="r num hide-sm">{p.tournaments}</td>
                      <td className="r num hide-xs">{p.wins}</td>
                      <td className="r">
                        <Money cents={p.netCents} signed />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <section className="section" aria-labelledby="hist-h" style={{ marginTop: 20 }}>
        <h2 id="hist-h">{t('stats.history')}</h2>
        {s.history.length === 0 ? (
          <div className="rows">
            <p className="empty">{t('stats.noData')}</p>
          </div>
        ) : (
          <div className="table-scroll panel" style={{ padding: 0 }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('stats.tournament')}</th>
                  <th className="r">{t('results.place')}</th>
                  <th className="r hide-sm">{t('results.prize')}</th>
                  <th className="r">{t('results.net')}</th>
                  <th className="r hide-sm">{t('stats.cumulative')}</th>
                </tr>
              </thead>
              <tbody>
                {[...s.history].reverse().map((h) => (
                  <tr key={h.tournamentId}>
                    <td>
                      <Link to={`/turnaukset/${h.tournamentId}/tulokset`}>{h.name}</Link>
                      <div className="hint">{dateOnly(h.finishedAt)}</div>
                    </td>
                    <td className="r num">
                      {h.placeFrom === h.placeTo ? `${h.placeFrom}.` : `${h.placeFrom}.–${h.placeTo}.`} / {h.entrants}
                    </td>
                    <td className="r hide-sm">
                      <Money cents={h.prizeCents} />
                    </td>
                    <td className="r">
                      <Money cents={h.netCents} signed />
                    </td>
                    <td className="r hide-sm">
                      <Money cents={h.cumulativeCents} signed />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
